// src/pages/LiveClientPortal.jsx
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../firebase";
import { onSnapshot, doc, updateDoc, collection, query, where } from "firebase/firestore";
import "../styles/liveClientPortal.css";

export default function LiveClientPortal() {
  const navigate = useNavigate();
  const user = auth.currentUser;
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState({});
  const [metrics, setMetrics] = useState({ visits: 0, leads: 0, conversions: 0 });
  const [activities, setActivities] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [draftProfile, setDraftProfile] = useState({});

  // Redirect if not authenticated
  useEffect(() => {
    if (!user) navigate("/login");
  }, [user, navigate]);

  // Real-time profile listener
  useEffect(() => {
    if (!user) return;
    const unsub = onSnapshot(
      doc(db, "Clients", user.uid),
      snap => {
        if (snap.exists()) {
          const data = snap.data();
          setProfile(data);
          setDraftProfile(data);
        }
        setLoading(false);
      }
    );
    return unsub;
  }, [user]);

  // Real-time metrics listener
  useEffect(() => {
    if (!user) return;
    const unsub = onSnapshot(
      doc(db, "ClientMetrics", user.uid),
      snap => {
        if (snap.exists()) setMetrics(snap.data());
      }
    );
    return unsub;
  }, [user]);

  // Real-time activities listener
  useEffect(() => {
    if (!user) return;
    const q = query(
      collection(db, "ClientActivities"),
      where("clientId", "==", user.uid)
    );
    const unsub = onSnapshot(q, snaps => {
      const list = [];
      snaps.forEach(doc => list.unshift({ id: doc.id, ...doc.data() }));
      setActivities(list);
    });
    return unsub;
  }, [user]);

  // Real-time campaigns listener
  useEffect(() => {
    if (!user) return;
    const q = query(
      collection(db, "Campaigns"),
      where("ownerId", "==", user.uid)
    );
    const unsub = onSnapshot(q, snaps => {
      const list = [];
      snaps.forEach(doc => list.push({ id: doc.id, ...doc.data() }));
      setCampaigns(list);
    });
    return unsub;
  }, [user]);

  const saveProfile = useCallback(async () => {
    if (!user) return;
    await updateDoc(doc(db, "Clients", user.uid), draftProfile);
    setEditMode(false);
  }, [user, draftProfile]);

  if (loading) return <div className="lcp-loading">Loading dashboard…</div>;

  return (
    <div className="lcp-container">
      <header className="lcp-header">
        <h1>Welcome, {profile.brandName || user.email}</h1>
        <button onClick={() => setEditMode(!editMode)} className="lcp-btn">
          {editMode ? "Cancel" : "Edit Profile"}
        </button>
        {editMode && (
          <button onClick={saveProfile} className="lcp-btn save">
            Save Changes
          </button>
        )}
      </header>

      {/* Profile Section */}
      <section className="lcp-section profile-section">
        <h2>Profile Information</h2>
        <div className="lcp-profile-grid">
          {["brandName","contactName","email","website","industry","goals"].map(field => (
            <div key={field} className="lcp-profile-item">
              <label>{field
                .replace(/([A-Z])/g, " $1")
                .replace(/^./, s => s.toUpperCase())}:</label>
              {editMode && field !== "email" ? (
                <input
                  type="text"
                  value={draftProfile[field] || ""}
                  onChange={e =>
                    setDraftProfile(prev => ({ ...prev, [field]: e.target.value }))
                  }
                />
              ) : (
                <p>{profile[field] || <em>Not set</em>}</p>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Metrics Section */}
      <section className="lcp-section metrics-section">
        <h2>Key Performance Indicators</h2>
        <div className="lcp-metrics-grid">
          <div className="lcp-metric-card">
            <h3>Visits</h3>
            <p>{metrics.visits.toLocaleString()}</p>
          </div>
          <div className="lcp-metric-card">
            <h3>Leads</h3>
            <p>{metrics.leads.toLocaleString()}</p>
          </div>
          <div className="lcp-metric-card">
            <h3>Conversions</h3>
            <p>{metrics.conversions.toLocaleString()}</p>
          </div>
        </div>
      </section>

      {/* Chart Placeholder */}
      <section className="lcp-section chart-section">
        <h2>Performance Over Time</h2>
        <div className="lcp-chart-placeholder">
          <p>Chart goes here</p>
        </div>
      </section>

      {/* Campaigns Section */}
      <section className="lcp-section campaigns-section">
        <h2>Active Campaigns</h2>
        <ul className="lcp-campaigns-list">
          {campaigns.length > 0 ? campaigns.map(c => (
            <li key={c.id}>
              <strong>{c.title}</strong><br/>
              Status: {c.status} • Budget: ${c.budget}
            </li>
          )) : (
            <li><em>No active campaigns</em></li>
          )}
        </ul>
      </section>

      {/* Activity Section */}
      <section className="lcp-section activity-section">
        <h2>Recent Activity</h2>
        <ul className="lcp-activity-list">
          {activities.length > 0 ? activities.slice(0,10).map(a => (
            <li key={a.id}>
              <time>{new Date(a.timestamp?.seconds * 1000).toLocaleString()}</time>
              <p>{a.text}</p>
            </li>
          )) : (
            <li><em>No recent activity</em></li>
          )}
        </ul>
      </section>
    </div>
  );
}
